<template>
  <div id="broker-commission-payment" class="bcp-page">
    <!-- Barre d'outils : periode + filtre commission payee / non payee -->
    <div class="bcp-toolbar">
      <div class="bcp-toolbar-group">
        <vs-button type="border" size="small" icon-pack="feather" icon="icon-arrow-left" :to="{ name: 'broker-check' }">
          Broker Check
        </vs-button>
        <div class="bcp-period">
          <feather-icon icon="CalendarIcon" svgClasses="h-4 w-4" class="bcp-period-icon" />
          <label for="bcp-from">Factures du</label>
          <input id="bcp-from" v-model="from" type="date" class="bcp-date" @keyup.enter="load" />
          <label for="bcp-cutoff">au</label>
          <input id="bcp-cutoff" v-model="cutoff" type="date" class="bcp-date" @keyup.enter="load" />
          <vs-button size="small" icon-pack="feather" icon="icon-refresh-cw" :disabled="loading" @click="load">
            Actualiser
          </vs-button>
        </div>
      </div>
      <div class="bcp-toolbar-group">
        <span class="bcp-filter-label">Commission</span>
        <div class="bcp-segmented" role="tablist">
          <button v-for="f in filters" :key="f.value" type="button" role="tab" :aria-selected="filter === f.value"
            :class="{ active: filter === f.value }" @click="filter = f.value">
            {{ f.label }}<span class="bcp-count">{{ filterCount(f.value) }}</span>
          </button>
        </div>
      </div>
    </div>
    <p class="bcp-hint">
      <feather-icon icon="InfoIcon" svgClasses="h-4 w-4" />
      Seul « Marquer comme réglé » passe une commission en payée ; générer le relevé PDF ne change rien au paiement.
      Une facture n'est commissionnée qu'une fois.
    </p>

    <!-- Conteneur stable : vs-popup deplace son element dans <body>, il ne doit jamais servir
         de point d'insertion aux blocs qui apparaissent apres le chargement. -->
    <div class="bcp-content">
      <div v-if="loading" class="bcp-empty">
        <feather-icon icon="LoaderIcon" svgClasses="h-6 w-6" class="bcp-spin" />
        <div>Calcul des commissions…</div>
      </div>
      <vs-alert v-else-if="loadError" color="danger" active="true" class="mb-4">{{ loadError }}</vs-alert>
      <div v-else-if="!visibleBrokers.length" class="bcp-empty">
        <feather-icon icon="InboxIcon" svgClasses="h-8 w-8" />
        <div>Aucune facture de broker {{ filterLabel }} entre le {{ formatDate(from) }} et le {{ formatDate(cutoff) }}.</div>
      </div>

      <template v-else>
        <!-- Chiffres cles -->
        <div class="bcp-kpis">
          <div class="bcp-kpi kpi-topay">
            <div class="bcp-kpi-label">Commission à payer</div>
            <div class="bcp-kpi-value">{{ money(grandTotal) }} €</div>
            <div class="bcp-kpi-sub">{{ grandSelected }} facture(s) cochée(s) sur {{ grandEligible }}</div>
          </div>
          <div class="bcp-kpi kpi-future">
            <div class="bcp-kpi-label">Commission future</div>
            <div class="bcp-kpi-value">{{ money(grandFuture) }} €</div>
            <div class="bcp-kpi-sub">{{ grandWaiting }} facture(s) en attente d'encaissement client</div>
          </div>
          <div class="bcp-kpi kpi-paid">
            <div class="bcp-kpi-label">Déjà payée sur la période</div>
            <div class="bcp-kpi-value">{{ money(grandPaid) }} €</div>
            <div class="bcp-kpi-sub">{{ grandPaidCount }} facture(s) réglée(s)</div>
          </div>
          <div class="bcp-kpi kpi-unpaid">
            <div class="bcp-kpi-label">Impayés clients du portefeuille (HT)</div>
            <div class="bcp-kpi-value">{{ money(grandUnpaid) }} €</div>
            <div class="bcp-kpi-sub" :class="{ 'pay-pending': grandPaidOut }">
              dont commission déjà versée : {{ money(grandPaidOut) }} €
            </div>
          </div>
        </div>

        <!-- Synthese -->
        <vx-card class="mb-base bcp-card" title="Synthèse par broker">
          <div class="overflow-x-auto">
            <table class="bcp-table bcp-summary">
              <thead>
                <tr>
                  <th>Broker</th>
                  <th>Déclenchement</th>
                  <th class="num" title="Factures cochées / factures dont la commission peut être payée">Cochées</th>
                  <th class="num">Commission à payer</th>
                  <th class="num" title="Factures depuis la date de départ du broker, pas encore encaissées : commission versée à l'encaissement">Commission future</th>
                  <th class="num">Déjà payée (période)</th>
                  <th class="num" title="Toutes les factures non soldées du portefeuille, toutes dates, avoirs non imputés compris (mêmes montants HT que l'écran Overdue)">Impayés clients (HT)</th>
                  <th class="num" title="Commission déjà versée au broker (ancien régime ou relevé réglé) alors que le client n'a pas payé">dont comm. déjà versée</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in visibleBrokers" :key="'s' + b.idbroker">
                  <td class="strong"><a :href="'#broker-' + b.idbroker">{{ b.name }}</a></td>
                  <td>
                    <span v-if="!b.settings.enabled" class="bcp-pill pill-danger">désactivé</span>
                    <span v-else class="bcp-pill" :class="b.settings.trigger_mode === 'payment' ? 'pill-warning' : 'pill-primary'">
                      {{ triggerLabel(b.settings.trigger_mode) }}
                    </span>
                  </td>
                  <td class="num">{{ selectedRows(b).length }} / {{ b.eligible.length }}</td>
                  <td class="num strong">{{ money(selectedTotals(b).commission_eur) }} €</td>
                  <td class="num">
                    <template v-if="b.waiting.length">
                      {{ money(sum(b.waiting, 'commission_eur')) }} €<span class="bcp-sub"> · {{ b.waiting.length }} fact.</span>
                    </template>
                    <span v-else class="bcp-muted">-</span>
                  </td>
                  <td class="num">
                    <template v-if="b.paid.length">{{ money(b.totals_paid.commission_eur) }} €</template>
                    <span v-else class="bcp-muted">-</span>
                  </td>
                  <td class="num">
                    <a v-if="b.unpaid && b.unpaid.length" :href="'#broker-' + b.idbroker" @click="openUnpaid(b)">
                      {{ money(b.unpaid_totals.ht_eur) }} €<span class="bcp-sub"> · {{ b.unpaid.length }} doc.</span>
                    </a>
                    <span v-else class="bcp-muted">-</span>
                  </td>
                  <td class="num" :class="{ 'pay-pending strong': b.unpaid_totals && b.unpaid_totals.commission_paid_out }">
                    <template v-if="b.unpaid_totals && b.unpaid_totals.commission_paid_out">{{ money(b.unpaid_totals.commission_paid_out) }} €</template>
                    <span v-else class="bcp-muted">-</span>
                  </td>
                </tr>
                <tr class="bcp-total">
                  <td colspan="3">Total</td>
                  <td class="num">{{ money(grandTotal) }} €</td>
                  <td class="num">{{ money(grandFuture) }} €</td>
                  <td class="num">{{ money(grandPaid) }} €</td>
                  <td class="num">{{ money(grandUnpaid) }} €</td>
                  <td class="num">{{ money(grandPaidOut) }} €</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="bcp-note">
            <feather-icon icon="InfoIcon" svgClasses="h-4 w-4" />
            <span>
              Impayés clients : toutes les factures non soldées du portefeuille, quelle que soit leur date, avoirs non imputés compris
              (mêmes montants HT que l'écran Overdue). Leur commission se répartit entre la « commission future » (factures depuis la
              date de départ, versée à l'encaissement) et la commission « déjà versée » (ancien régime ou relevé réglé) alors que le
              client n'a pas payé. Les pénalités de retard ne sont jamais commissionnées.
            </span>
          </p>
        </vx-card>

        <!-- Un bloc par broker -->
        <vx-card v-for="b in visibleBrokers" :key="b.idbroker" :id="'broker-' + b.idbroker" class="mb-base bcp-card bcp-broker">
          <div class="bcp-broker-head">
            <div class="bcp-broker-id">
              <div class="bcp-avatar">{{ initials(b.name) }}</div>
              <div>
                <div class="flex flex-wrap items-center">
                  <h3 class="bcp-broker-name">{{ b.name }}</h3>
                  <span class="bcp-pill" :class="b.settings.trigger_mode === 'payment' ? 'pill-warning' : 'pill-primary'">
                    {{ triggerLabel(b.settings.trigger_mode) }}
                  </span>
                  <span v-if="!b.settings.enabled" class="bcp-pill pill-danger">désactivé</span>
                  <span v-if="!b.isbroker" class="bcp-pill pill-danger">pas marqué broker</span>
                </div>
                <div class="bcp-rules">
                  <span><feather-icon icon="TruckIcon" svgClasses="h-3 w-3" />
                    {{ b.settings.transport_eur_per_m3 > 0 ? 'Transport déduit ' + money(b.settings.transport_eur_per_m3, 0) + ' €/m³' : 'Transport inclus' }}
                  </span>
                  <span><feather-icon icon="PercentIcon" svgClasses="h-3 w-3" />
                    {{ pct(b.settings.pct_default) }}<template v-if="b.settings.pct_aboute !== b.settings.pct_default"> · aboutés {{ pct(b.settings.pct_aboute) }}</template>
                  </span>
                  <span><feather-icon icon="CalendarIcon" svgClasses="h-3 w-3" /> depuis le {{ formatDate(b.settings.start_date) }}</span>
                  <span :class="{ 'pay-paid': mailTarget(b).direct }" :title="mailTarget(b).direct ? 'Envoi direct au broker' : 'Phase de test : relevé envoyé pour validation, puis transféré au broker'">
                    <feather-icon icon="MailIcon" svgClasses="h-3 w-3" /> {{ mailTarget(b).label }}
                  </span>
                  <span title="Langue de la fiche du broker ; relevé et mail sortent dans cette langue (FR, EN, DE ; autres en anglais)">
                    <feather-icon icon="GlobeIcon" svgClasses="h-3 w-3" /> relevé en {{ (b.language || 'fr').toUpperCase() }}
                  </span>
                </div>
              </div>
            </div>
            <div class="bcp-actions">
              <vs-button type="flat" size="small" icon-pack="feather" icon="icon-settings" @click="openSettings(b)">
                Paramètres
              </vs-button>
              <vs-button type="border" size="small" icon-pack="feather" icon="icon-search"
                :disabled="!selectedRows(b).length || filter === 'paid' || busy" @click="verificationPdf(b)">
                PDF de vérification
              </vs-button>
              <vs-button type="border" size="small" color="dark" icon-pack="feather" icon="icon-check-square"
                :disabled="!canGenerate(b) || busy" @click="confirmGenerate(b, 'settlement')">
                Marquer comme réglé
              </vs-button>
              <vs-button size="small" color="success" icon-pack="feather" icon="icon-file-text"
                :disabled="!canGenerate(b) || busy" @click="confirmGenerate(b, 'statement')">
                Générer le relevé PDF · {{ money(selectedTotals(b).commission_eur) }} €
              </vs-button>
            </div>
          </div>

          <vs-alert v-if="!b.persisted" color="warning" active="true" class="mb-3">
            Règles par défaut, jamais enregistrées pour ce broker : vérifiez-les et enregistrez-les (Paramètres) avant de pouvoir générer.
          </vs-alert>
          <vs-alert v-if="b.first_use && b.eligible.length && filter !== 'paid'" color="warning" active="true" class="mb-3">
            Premier relevé de ce broker : rien n'est coché. Si certaines de ces factures ont déjà été commissionnées via l'Excel,
            cochez-les et cliquez « Marquer comme réglé » ; cochez ensuite les factures à payer et générez le relevé.
          </vs-alert>
          <vs-alert v-if="b.anomalies.length" color="danger" active="true" class="mb-3">
            Déjà commissionnées mais {{ b.anomalies.some(a => a.reason === 'inactive') ? 'désactivées ou ' : '' }}de nouveau impayées :
            <span v-for="(a, i) in b.anomalies" :key="'a' + a.idoperation">
              {{ i ? ', ' : '' }}{{ a.invoice_no || '#' + a.idoperation }} ({{ a.reason === 'inactive' ? 'désactivée' : 'impayée' }}, {{ a.statement_no }}, {{ money(a.commission_eur) }} €)
            </span>
          </vs-alert>

          <!-- Factures de la periode, selon le filtre -->
          <div v-if="rowsFor(b).length" class="bcp-scroll">
            <table class="bcp-table bcp-invoices">
              <thead>
                <tr>
                  <th class="chk">
                    <input v-if="filter !== 'paid' && b.eligible.length" type="checkbox" :checked="allSelected(b)"
                      title="Tout cocher / décocher" @change="toggleAll(b, $event.target.checked)" />
                  </th>
                  <th>COMM. PAYÉE ?</th>
                  <th>INV. NR.</th>
                  <th>STATUS</th>
                  <th>TYPE</th>
                  <th>QUOTATION</th>
                  <th>DATE</th>
                  <th>CLIENT</th>
                  <th class="num">VOL. (m³)</th>
                  <th class="num">PRICE (EUR)</th>
                  <th v-if="hasRon(b)" class="num">PRICE (RON)</th>
                  <th>COUNTRY</th>
                  <th>FSC</th>
                  <th class="num">TENURE</th>
                  <th class="num">TRANSPORT</th>
                  <th class="num">CA HORS TRANSPORT</th>
                  <th class="num">%</th>
                  <th class="num sticky-end">COMMISSION</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in rowsFor(b)" :key="r._key"
                  :class="{ 'bcp-off': r._state === 'to_pay' && !isSelected(b, r), 'bcp-dim': r._state === 'waiting' || r._state === 'before' }">
                  <td class="chk">
                    <input v-if="r._state === 'to_pay'" type="checkbox" :checked="isSelected(b, r)" @change="toggleRow(b, r, $event.target.checked)" />
                  </td>
                  <td :title="commTitle(r)"><span class="bcp-pill" :class="commClass(r)">{{ commLabel(r) }}</span></td>
                  <td><router-link :to="'/invoice/' + r.idoperation">{{ r.invoice_no }}</router-link></td>
                  <td :class="r.status_name === 'Paid' ? 'pay-paid' : 'pay-partial'">{{ r.status_name === 'Paid' ? 'Paid' : 'Pending' }}</td>
                  <td>{{ typeLabel(r.doc_type) }}</td>
                  <td>
                    <router-link v-if="r.idquotation" :to="'/quotation/' + r.idquotation">#{{ r.idquotation }}</router-link>
                    <span v-else class="bcp-muted">-</span>
                  </td>
                  <td>{{ shortDate(r.invoice_date) }}</td>
                  <td class="client" :title="r.client">{{ r.client || '-' }}</td>
                  <td class="num">{{ r.volume_m3 ? r.volume_m3.toFixed(2) : '-' }}</td>
                  <td class="num">{{ money(r.amount_eur) }}</td>
                  <td v-if="hasRon(b)" class="num">{{ r.currency === 'RON' ? money(r.amount_ron) : 'N/A' }}</td>
                  <td>{{ r.country_code || '-' }}</td>
                  <td>{{ r.fsc ? 'FSC' : '-' }}</td>
                  <td class="num">{{ r.tenure_months || '-' }}</td>
                  <td class="num">{{ r.transport_eur ? money(r.transport_eur) : '-' }}</td>
                  <td class="num">{{ money(r.base_eur) }}</td>
                  <td class="num">{{ pct(r.pct_effective) }}</td>
                  <td class="num strong sticky-end">
                    <span class="bcp-comm">
                      {{ money(r.commission_eur) }}
                      <vx-tooltip v-if="explain(r)" :text="explain(r)" position="left" class="inline-flex">
                        <feather-icon icon="InfoIcon" svgClasses="h-4 w-4" :class="(r.warnings || []).length ? 'text-danger' : 'text-grey'" />
                      </vx-tooltip>
                    </span>
                  </td>
                </tr>
                <tr v-if="filter !== 'paid' && b.eligible.length" class="bcp-total">
                  <td></td>
                  <td colspan="7">À payer, cochées ({{ selectedRows(b).length }})</td>
                  <td class="num">{{ selectedTotals(b).volume_m3.toFixed(2) }}</td>
                  <td class="num">{{ money(selectedTotals(b).amount_eur) }}</td>
                  <td :colspan="hasRon(b) ? 4 : 3"></td>
                  <td class="num">{{ money(selectedTotals(b).transport_eur) }}</td>
                  <td class="num">{{ money(selectedTotals(b).base_eur) }}</td>
                  <td></td>
                  <td class="num sticky-end">{{ money(selectedTotals(b).commission_eur) }}</td>
                </tr>
                <tr v-if="filter !== 'paid' && b.waiting.length" class="bcp-total bcp-total-waiting">
                  <td></td>
                  <td colspan="8">En attente d'encaissement client ({{ b.waiting.length }}) : commission future</td>
                  <td class="num">{{ money(sum(b.waiting, 'amount_eur')) }}</td>
                  <td :colspan="hasRon(b) ? 4 : 3"></td>
                  <td class="num">{{ money(sum(b.waiting, 'transport_eur')) }}</td>
                  <td class="num">{{ money(sum(b.waiting, 'base_eur')) }}</td>
                  <td></td>
                  <td class="num sticky-end">{{ money(sum(b.waiting, 'commission_eur')) }}</td>
                </tr>
                <tr v-if="filter !== 'nonpaid' && b.paid.length" class="bcp-total bcp-total-paid">
                  <td></td>
                  <td colspan="7">Commission déjà payée ({{ b.paid.length }})</td>
                  <td class="num">{{ b.totals_paid.volume_m3.toFixed(2) }}</td>
                  <td class="num">{{ money(b.totals_paid.amount_eur) }}</td>
                  <td :colspan="hasRon(b) ? 4 : 3"></td>
                  <td class="num">{{ money(b.totals_paid.transport_eur) }}</td>
                  <td class="num">{{ money(b.totals_paid.base_eur) }}</td>
                  <td></td>
                  <td class="num sticky-end">{{ money(b.totals_paid.commission_eur) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="bcp-empty-inline">Aucune facture {{ filterLabel }} entre le {{ formatDate(from) }} et le {{ formatDate(cutoff) }}.</p>

          <!-- Factures clients impayees du portefeuille (toutes dates) : le broker relance ses clients -->
          <div v-if="b.unpaid && b.unpaid.length" class="bcp-section">
            <button type="button" class="bcp-section-head" :aria-expanded="isOpen('u', b.idbroker)" @click="toggleOpen('u', b.idbroker)">
              <feather-icon :icon="isOpen('u', b.idbroker) ? 'ChevronDownIcon' : 'ChevronRightIcon'" svgClasses="h-4 w-4" />
              <span class="bcp-section-title">Factures clients impayées du portefeuille</span>
              <span class="bcp-count">{{ b.unpaid.length }}</span>
              <span class="bcp-stats">
                <span class="bcp-stat">HT <b>{{ money(b.unpaid_totals.ht_eur) }} €</b></span>
                <span class="bcp-stat">encaissé <b>{{ remainingText(b.unpaid_totals.collected) }}</b></span>
                <span class="bcp-stat">reste dû <b>{{ remainingText(b.unpaid_totals.remaining) }}</b></span>
                <span v-if="b.unpaid_totals.disputes" class="bcp-stat stat-danger">{{ b.unpaid_totals.disputes }} en litige</span>
                <span v-if="b.unpaid_totals.commission_paid_out_count" class="bcp-stat stat-danger">
                  commission déjà versée sur {{ b.unpaid_totals.commission_paid_out_count }} facture(s) : <b>{{ money(b.unpaid_totals.commission_paid_out) }} €</b>
                </span>
              </span>
            </button>
            <div v-if="isOpen('u', b.idbroker)" class="bcp-scroll mt-2">
              <table class="bcp-table">
                <thead>
                  <tr>
                    <th>INV. NR.</th><th>TYPE</th><th>DATE</th><th>ÉCHÉANCE</th><th>CLIENT</th>
                    <th class="num">MONTANT HT</th><th class="num">ENCAISSÉ</th><th class="num">RESTE DÛ</th><th class="num">RETARD (J)</th>
                    <th class="num">COMMISSION</th><th>COMMISSION : STATUT</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in b.unpaid" :key="'u' + u.idoperation">
                    <td><router-link :to="'/invoice/' + u.idoperation">{{ u.invoice_no }}</router-link></td>
                    <td>
                      {{ u.doc_type === 'majoration' ? 'Pénalité de retard' : typeLabel(u.doc_type) }}
                      <span v-if="u.dispute" class="dispute-badge" :title="u.dispute_comment || 'Facture en litige'">DISPUTE</span>
                    </td>
                    <td>{{ shortDate(u.invoice_date) }}</td>
                    <td>{{ shortDate(u.due_date) }}</td>
                    <td class="client" :title="u.client">{{ u.client || '-' }}</td>
                    <td class="num">{{ money(u.ht_eur) }}</td>
                    <td class="num">{{ u.doc_type === 'creditnote' ? '-' : money(u.collected) + (u.currency === 'RON' ? ' RON' : ' €') }}</td>
                    <td class="num">{{ money(u.remaining) }} {{ u.currency === 'RON' ? 'RON' : '€' }}</td>
                    <td class="num" :class="{ 'pay-pending': u.days_overdue > 60 && u.doc_type !== 'creditnote' }">
                      {{ u.doc_type === 'creditnote' ? '-' : (u.days_overdue > 0 ? u.days_overdue : 'non échue') }}
                    </td>
                    <td class="num">{{ u.commission_status === 'none' || u.commission_status === 'credit_note' ? '-' : money(u.commission_eur) }}</td>
                    <td><span class="bcp-pill" :class="unpaidStatusClass(u)">{{ unpaidStatusLabel(u) }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Historique -->
          <div v-if="b.history.length" class="bcp-section">
            <button type="button" class="bcp-section-head" :aria-expanded="isOpen('h', b.idbroker)" @click="toggleOpen('h', b.idbroker)">
              <feather-icon :icon="isOpen('h', b.idbroker) ? 'ChevronDownIcon' : 'ChevronRightIcon'" svgClasses="h-4 w-4" />
              <span class="bcp-section-title">Relevés et règlements</span>
              <span class="bcp-count">{{ b.history.length }}</span>
              <span class="bcp-stats">
                <span v-if="toSettleCount(b)" class="bcp-stat stat-warning">{{ toSettleCount(b) }} relevé(s) à régler</span>
              </span>
            </button>
            <div v-if="isOpen('h', b.idbroker)" class="bcp-scroll mt-2">
              <table class="bcp-table">
                <thead>
                  <tr>
                    <th>N°</th><th>TYPE</th><th>JUSQU'AU</th><th>ÉMIS LE</th><th>PAR</th>
                    <th class="num">FACTURES</th><th class="num">COMMISSION (€)</th><th>ÉTAT</th><th>MAIL</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in b.history" :key="'h' + s.id" :class="{ 'bcp-voided': s.voided_at }">
                    <td>{{ s.statement_no }}</td>
                    <td>{{ kindLabel(s.kind) }}</td>
                    <td>{{ formatDate(s.cutoff_date) }}</td>
                    <td>{{ formatDateTime(s.generated_at) }}</td>
                    <td>{{ s.generated_by || '-' }}</td>
                    <td class="num">{{ s.nb_lines }}</td>
                    <td class="num">{{ money(s.total_commission_eur) }}</td>
                    <td><span class="bcp-pill" :class="stateClass(s)">{{ stateLabel(s) }}</span></td>
                    <td>
                      <span v-if="s.kind !== 'statement'" class="bcp-muted">-</span>
                      <span v-else-if="s.mail_error" class="pay-pending" :title="s.mail_error">échec : {{ s.mail_error }}</span>
                      <span v-else-if="s.mailed_at" class="pay-paid">{{ s.mailed_to }} · {{ formatDateTime(s.mailed_at) }}</span>
                      <span v-else class="bcp-muted">pas envoyé</span>
                    </td>
                    <td class="actions">
                      <span v-if="s.voided_at" class="bcp-muted">annulé le {{ formatDateTime(s.voided_at) }} ({{ s.voided_by || '-' }})</span>
                      <div v-else class="flex items-center justify-end">
                        <vs-button v-if="s.has_pdf" size="small" type="border" icon-pack="feather" icon="icon-download" class="mr-2"
                          @click="downloadPdf(s)">PDF</vs-button>
                        <vs-button v-if="s.has_pdf" size="small" type="border" icon-pack="feather" icon="icon-mail" class="mr-2"
                          :disabled="mailing === s.id" @click="confirmMail(b, s)">{{ s.mailed_at ? 'Renvoyer' : 'Envoyer' }}</vs-button>
                        <vs-button v-if="s.kind === 'statement' && !s.settled_by_no" size="small" color="success" icon-pack="feather"
                          icon="icon-check-square" class="mr-2" :disabled="busy" @click="confirmSettle(s)">Marquer comme réglé</vs-button>
                        <vs-button v-if="canVoid(b, s)" size="small" type="border" color="danger" icon-pack="feather" icon="icon-x"
                          @click="confirmVoid(s)">Annuler</vs-button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </vx-card>
      </template>
    </div>

    <!-- Parametres d'un broker -->
    <vs-popup :title="'Règles de commission : ' + (settingsForm.name || '')" :active.sync="settingsOpen">
      <div class="bcp-form">
        <div class="mb-4 flex items-center">
          <vs-switch v-model="settingsForm.enabled" class="mr-3" />
          <span>{{ settingsForm.enabled ? 'Commissionné' : 'Désactivé (aucun relevé)' }}</span>
        </div>
        <div class="mb-4">
          <label class="vs-input--label">Déclenchement</label>
          <div class="flex">
            <vs-radio v-model="settingsForm.trigger_mode" vs-value="invoice" class="mr-6">À la facturation</vs-radio>
            <vs-radio v-model="settingsForm.trigger_mode" vs-value="payment">Au paiement du client</vs-radio>
          </div>
        </div>
        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <vs-input type="number" step="1" min="0" class="w-full" label="Transport déduit €/m³ (0 = inclus)" v-model="settingsForm.transport_eur_per_m3" />
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <vs-input type="date" class="w-full" label="Commissionner les factures à partir du" v-model="settingsForm.start_date" />
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <vs-input type="number" step="0.5" min="0" max="100" class="w-full" label="Commission (%)" v-model="settingsForm.pct_default" />
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <vs-input type="number" step="0.5" min="0" max="100" class="w-full" label="Commission produits aboutés (%)" v-model="settingsForm.pct_aboute" />
          </div>
        </div>
        <div class="mb-4">
          <label class="vs-input--label">Langue du relevé et du mail (fiche du broker)</label>
          <select v-model="settingsForm.idlanguage" class="bcp-select">
            <option :value="null" disabled>— langue de la fiche non renseignée —</option>
            <option v-for="l in languages" :key="l.id" :value="l.id">{{ l.name }} ({{ l.code }})</option>
          </select>
          <p class="bcp-help mt-1">FR, EN et DE sont traduits ; les autres langues sortent en anglais. Modifie la fiche du broker.</p>
        </div>
        <div class="mb-4 flex items-center">
          <vs-switch v-model="settingsForm.mail_to_broker" class="mr-3" />
          <span v-if="settingsForm.mail_to_broker">
            Relevé envoyé directement au broker ({{ settingsForm.email || 'aucune adresse sur sa fiche : repli sur ' + mailContact }}), {{ mailContact }} en copie cachée
          </span>
          <span v-else>Phase de test : relevé envoyé à {{ mailContact }}, qui le transfère au broker</span>
        </div>
        <p class="bcp-help mb-4">
          Aboutés = catégories {{ abouteIds.join(', ') }} (lames de terrasse, lambourdes et bardages aboutés).
          Une facture antérieure à la date de départ n'est jamais proposée : celles déjà réglées via l'Excel peuvent aussi être
          marquées « déjà réglé » depuis la liste.
        </p>
        <div class="flex justify-end">
          <vs-button type="border" class="mr-3" @click="settingsOpen = false">Fermer</vs-button>
          <vs-button :disabled="savingSettings" @click="saveSettings">Enregistrer</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import axios from '@/axios.js'

const TYPE_LABELS = { final: 'Final', advance: 'Advance', creditnote: 'Credit Note' }
// Libelle court (pastille) + explication complete (infobulle).
const REASON_LABELS = {
  not_paid: ['Non · attente client', 'Commission due à l\'encaissement : le client n\'a pas encore payé cette facture'],
  origin_not_commissioned: ['Non · origine non comm.', 'Avoir : la facture d\'origine n\'est pas encore commissionnée'],
  origin_missing: ['Non · sans origine', 'Avoir sans facture d\'origine']
}
const FILTERS = [
  { value: 'nonpaid', label: 'Non payée' },
  { value: 'paid', label: 'Payée' },
  { value: 'all', label: 'Toutes' }
]
const MONEY = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const MONEY0 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })

function endOfPreviousMonth () {
  const d = new Date()
  const last = new Date(d.getFullYear(), d.getMonth(), 0)
  const mm = String(last.getMonth() + 1).padStart(2, '0')
  const dd = String(last.getDate()).padStart(2, '0')
  return `${last.getFullYear()}-${mm}-${dd}`
}

// Somme en centimes : le total affiche est exactement la somme des lignes affichees.
function sumCents (rows, field) {
  let cents = 0
  for (const r of rows) cents += Math.round((Number(r[field]) || 0) * 100)
  return cents / 100
}

async function errorMessage (err, fallback) {
  const data = err && err.response && err.response.data
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text())
      if (parsed && parsed.message) return parsed.message
    } catch (e) {
      /* corps illisible : message par defaut */
    }
  }
  return (data && data.message) || fallback
}

export default {
  name: 'BrokerCommissionPayment',
  data () {
    const cutoff = endOfPreviousMonth()
    return {
      cutoff,
      // Toute l'annee par defaut : rien d'a payer n'est masque (ex. ventes de
      // juillet de Michael encaissees seulement maintenant).
      from: `${cutoff.slice(0, 4)}-01-01`,
      filter: 'nonpaid',
      filters: FILTERS,
      loading: true,
      loadError: null,
      brokers: [],
      abouteIds: [],
      languages: [],
      mailContact: '',
      mailing: null,
      busy: false,
      selection: {}, // idbroker -> { idoperation: true }
      open: {}, // 'w12' / 'h12' -> true
      settingsOpen: false,
      savingSettings: false,
      settingsForm: {}
    }
  },
  computed: {
    grandTotal () {
      return Math.round(this.brokers.reduce((s, b) => s + Math.round(this.selectedTotals(b).commission_eur * 100), 0)) / 100
    },
    grandFuture () {
      return Math.round(this.brokers.reduce((s, b) => s + Math.round(this.sum(b.waiting, 'commission_eur') * 100), 0)) / 100
    },
    grandUnpaid () {
      return Math.round(this.brokers.reduce((s, b) => s + Math.round(((b.unpaid_totals && b.unpaid_totals.ht_eur) || 0) * 100), 0)) / 100
    },
    grandPaidOut () {
      return Math.round(this.brokers.reduce((s, b) => s + Math.round(((b.unpaid_totals && b.unpaid_totals.commission_paid_out) || 0) * 100), 0)) / 100
    },
    grandSelected () {
      return this.brokers.reduce((n, b) => n + this.selectedRows(b).length, 0)
    },
    grandEligible () {
      return this.brokers.reduce((n, b) => n + b.eligible.length, 0)
    },
    grandWaiting () {
      return this.brokers.reduce((n, b) => n + b.waiting.length, 0)
    },
    grandPaidCount () {
      return this.brokers.reduce((n, b) => n + b.paid.length, 0)
    },
    grandPaid () {
      return Math.round(this.brokers.reduce((s, b) => s + Math.round(b.totals_paid.commission_eur * 100), 0)) / 100
    },
    // Un broker n'apparait que s'il a des factures pour ce filtre (ou des releves a consulter).
    visibleBrokers () {
      return this.brokers.filter((b) => this.rowsFor(b).length || b.history.length || (b.unpaid && b.unpaid.length))
    },
    filterLabel () {
      return { nonpaid: 'à commission non payée', paid: 'à commission payée', all: '' }[this.filter]
    }
  },
  methods: {
    async load () {
      this.loading = true
      this.loadError = null
      try {
        const res = await axios.get('/brokercommission/preview', { params: { cutoff: this.cutoff, from: this.from } })
        this.brokers = res.data.brokers || []
        this.abouteIds = res.data.aboute_category_ids || []
        this.languages = res.data.languages || []
        this.mailContact = res.data.mail_contact || ''
        const selection = {}
        for (const b of this.brokers) {
          selection[b.idbroker] = {}
          // Premiere utilisation : rien de coche, pour ne pas repayer ce que l'Excel a deja regle.
          if (!b.first_use) for (const r of b.eligible) selection[b.idbroker][r.idoperation] = true
        }
        this.selection = selection
      } catch (err) {
        this.loadError = await errorMessage(err, 'Impossible de calculer les commissions.')
      } finally {
        this.loading = false
      }
    },

    // ---- Selection ----
    isSelected (b, r) {
      return !!(this.selection[b.idbroker] && this.selection[b.idbroker][r.idoperation])
    },
    setSelected (b, id, value) {
      if (!this.selection[b.idbroker]) this.$set(this.selection, b.idbroker, {})
      this.$set(this.selection[b.idbroker], id, value)
    },
    toggleRow (b, r, checked) {
      this.setSelected(b, r.idoperation, checked)
      // Un avoir ne part jamais sans sa facture d'origine encore en attente.
      if (checked && r.requires) this.setSelected(b, r.requires, true)
      if (!checked) {
        for (const other of b.eligible) {
          if (other.requires === r.idoperation) this.setSelected(b, other.idoperation, false)
        }
      }
    },
    allSelected (b) {
      return b.eligible.length > 0 && b.eligible.every((r) => this.isSelected(b, r))
    },
    toggleAll (b, checked) {
      for (const r of b.eligible) this.setSelected(b, r.idoperation, checked)
    },
    selectedRows (b) {
      return b.eligible.filter((r) => this.isSelected(b, r))
    },
    selectedTotals (b) {
      const rows = this.selectedRows(b)
      let vol = 0
      for (const r of rows) vol += Math.round((Number(r.volume_m3) || 0) * 10000)
      return {
        amount_eur: sumCents(rows, 'amount_eur'),
        transport_eur: sumCents(rows, 'transport_eur'),
        base_eur: sumCents(rows, 'base_eur'),
        commission_eur: sumCents(rows, 'commission_eur'),
        volume_m3: vol / 10000
      }
    },
    canGenerate (b) {
      return this.filter !== 'paid' && b.persisted && b.settings.enabled && this.selectedRows(b).length > 0
    },

    // ---- Filtre commission payee / non payee ----
    rowsFor (b) {
      const tag = (rows, state) => rows.map((r) => Object.assign({ _state: state, _key: state + r.idoperation }, r))
      let rows = []
      if (this.filter !== 'paid') rows = rows.concat(tag(b.eligible, 'to_pay'), tag(b.waiting, 'waiting'))
      if (this.filter !== 'nonpaid') rows = rows.concat(tag(b.paid, 'paid'))
      if (this.filter === 'all') rows = rows.concat(tag(b.before, 'before'))
      return rows.sort((x, y) => {
        if (x.invoice_date !== y.invoice_date) return x.invoice_date < y.invoice_date ? -1 : 1
        return String(x.invoice_no).localeCompare(String(y.invoice_no))
      })
    },
    filterCount (value) {
      return this.brokers.reduce((n, b) => {
        if (value === 'nonpaid') return n + b.eligible.length + b.waiting.length
        if (value === 'paid') return n + b.paid.length
        return n + b.eligible.length + b.waiting.length + b.paid.length + b.before.length
      }, 0)
    },
    commLabel (r) {
      if (r._state === 'to_pay') return r.pending_statement_no ? `Non · sur ${r.pending_statement_no}` : 'Non · à payer'
      if (r._state === 'waiting') return (REASON_LABELS[r.reason] || ['Non · en attente'])[0]
      if (r._state === 'before') return 'Hors ERP'
      if (r.statement_kind === 'opening') return 'Oui · hors ERP'
      return `Oui · ${r.source_statement_no || r.statement_no}`
    },
    commTitle (r) {
      if (r._state === 'to_pay' && r.pending_statement_no) return `Figure sur le relevé ${r.pending_statement_no}, pas encore marqué comme réglé`
      if (r._state === 'to_pay') return 'Commission à payer : cochez pour l\'inclure dans le relevé ou le règlement'
      if (r._state === 'waiting') return (REASON_LABELS[r.reason] || ['', ''])[1]
      if (r._state === 'paid' && r.statement_kind === 'opening') return `Réglée hors ERP (${r.statement_no})`
      if (r._state === 'paid') return `Réglé par ${r.statement_no} le ${this.formatDateTime(r.generated_at)}`
      if (r._state === 'before') return 'Facture antérieure à la date de départ du broker : commission gérée hors ERP (Excel). Avancez la date de départ dans Paramètres pour la commissionner ici.'
      return ''
    },
    commClass (r) {
      if (r._state === 'to_pay') return r.pending_statement_no ? 'pill-warning-outline' : 'pill-warning'
      if (r._state === 'paid') return 'pill-success'
      if (r._state === 'waiting') return 'pill-amber'
      return 'pill-muted'
    },

    // ---- Generation ----
    confirmGenerate (b, kind) {
      const rows = this.selectedRows(b)
      const total = this.money(this.selectedTotals(b).commission_eur)
      const settleText = `La commission de ${rows.length} facture(s) de ${b.name} (${total} €) sera enregistrée comme PAYÉE au broker. Ces factures ne seront plus proposées.`
      const statementText = `Relevé de ${b.name} : ${rows.length} facture(s), ${total} € de commission, jusqu'au ${this.formatDate(this.cutoff)}. Le PDF sera envoyé par mail à ${this.mailTarget(b).to}. Les factures restent « non payées » jusqu'à « Marquer comme réglé ».`
      this.$vs.dialog({
        type: 'confirm',
        color: kind === 'settlement' ? 'dark' : 'success',
        title: kind === 'settlement' ? 'Marquer comme réglé' : 'Générer le relevé',
        text: kind === 'settlement' ? settleText : statementText,
        acceptText: kind === 'settlement' ? 'Marquer réglé' : 'Générer',
        cancelText: 'Annuler',
        accept: () => this.generate(b, kind, rows)
      })
    },
    async generate (b, kind, rows) {
      this.busy = true
      try {
        const res = await axios.post('/brokercommission/statements', {
          idbroker: b.idbroker,
          cutoff: this.cutoff,
          kind,
          ids: rows.map((r) => r.idoperation)
        })
        const mail = res.data.mail
        if (kind === 'settlement') this.notify('success', 'Commissions réglées', `${res.data.statement_no} · ${this.money(res.data.total_commission_eur)} €`)
        else if (mail && mail.ok) this.notify('success', `Relevé ${res.data.statement_no} généré`, `PDF envoyé par mail à ${mail.to}`)
        else this.notify('warning', `Relevé ${res.data.statement_no} généré, mail NON parti`, `${(mail && mail.error) || 'erreur inconnue'} : utilisez « Envoyer » dans l'historique.`)
        if (kind === 'statement' && res.data.has_pdf) await this.downloadPdf(res.data)
        await this.load()
      } catch (err) {
        this.notify('danger', 'Refusé', await errorMessage(err, 'L\'opération a échoué.'))
      } finally {
        this.busy = false
      }
    },
    // PDF de controle : une ligne par produit pour les factures cochees. Rien n'est enregistre.
    async verificationPdf (b) {
      this.busy = true
      try {
        const res = await axios.post('/brokercommission/verification-pdf', {
          idbroker: b.idbroker,
          cutoff: this.cutoff,
          ids: this.selectedRows(b).map((r) => r.idoperation)
        }, { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `verification_commissions_${b.name}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.notify('danger', 'PDF de vérification indisponible', await errorMessage(err, 'Le PDF n\'a pas pu être généré.'))
      } finally {
        this.busy = false
      }
    },
    confirmSettle (s) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'success',
        title: 'Marquer le relevé comme réglé',
        text: `La commission du relevé ${s.statement_no} (${s.nb_lines} facture(s), ${this.money(s.total_commission_eur)} €) sera enregistrée comme PAYÉE au broker, aux montants du PDF.`,
        acceptText: 'Marquer réglé',
        cancelText: 'Annuler',
        accept: () => this.settleStatement(s)
      })
    },
    async settleStatement (s) {
      this.busy = true
      try {
        const res = await axios.post(`/brokercommission/statements/${s.id}/settle`)
        const skipped = res.data.skipped ? ` (${res.data.skipped} facture(s) déjà réglée(s) par ailleurs, ignorée(s))` : ''
        this.notify('success', `${s.statement_no} réglé`, `${res.data.statement_no} · ${this.money(res.data.total_commission_eur)} €${skipped}`)
        await this.load()
      } catch (err) {
        this.notify('danger', 'Règlement refusé', await errorMessage(err, 'L\'opération a échoué.'))
      } finally {
        this.busy = false
      }
    },
    unpaidStatusLabel (u) {
      if (u.commission_status === 'credit_note') return `Avoir à imputer${u.origin_invoice_no ? ` (sur ${u.origin_invoice_no})` : ''}`
      return {
        paid: `Versée (${u.settled_no})`,
        paid_legacy: 'Versée (ancien régime)',
        pending: 'Due à l\'encaissement',
        to_pay: 'À verser',
        none: 'Pénalité, sans commission'
      }[u.commission_status] || u.commission_status
    },
    // Commission deja versee alors que la creance reste ouverte : le cas sensible.
    unpaidStatusClass (u) {
      if (u.commission_status === 'paid' || u.commission_status === 'paid_legacy') return 'pill-danger'
      if (u.commission_status === 'to_pay') return 'pill-warning'
      if (u.commission_status === 'credit_note') return 'pill-info'
      return 'pill-muted'
    },
    // Montants par devise : { EUR: x, RON: y } -> "x € + y RON".
    remainingText (byCurrency) {
      const rem = byCurrency || {}
      // Une devise a zero n'est affichee que si c'est la seule.
      const keys = Object.keys(rem).filter((cur) => Number(rem[cur]) !== 0)
      const parts = keys.map((cur) => `${this.money(rem[cur])} ${cur === 'RON' ? 'RON' : '€'}`)
      return parts.length ? parts.join(' + ') : '0,00 €'
    },
    kindLabel (kind) {
      return { statement: 'Relevé PDF', settlement: 'Règlement', opening: 'Déjà réglé (reprise)' }[kind] || kind
    },
    stateLabel (s) {
      if (s.voided_at) return 'Annulé'
      if (s.kind === 'statement') return s.settled_by_no ? `Réglé (${s.settled_by_no})` : 'À régler'
      if (s.kind === 'settlement') return s.settles_no ? `Règle ${s.settles_no}` : 'Lignes cochées'
      return 'Payé hors ERP'
    },
    stateClass (s) {
      if (s.voided_at) return 'pill-muted'
      if (s.kind === 'statement' && !s.settled_by_no) return 'pill-warning'
      return 'pill-success'
    },
    // Releves PDF emis, pas encore regles ni annules.
    toSettleCount (b) {
      return b.history.filter((s) => !s.voided_at && s.kind === 'statement' && !s.settled_by_no).length
    },
    async downloadPdf (s) {
      try {
        const res = await axios.get(`/brokercommission/statements/${s.id}/pdf`, { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', s.pdf_filename || `${s.statement_no}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.notify('danger', 'PDF indisponible', await errorMessage(err, 'Le PDF n\'a pas pu être téléchargé.'))
      }
    },
    // Destinataire du releve, meme regle que le serveur (brokerCommission/mail.js).
    mailTarget (b) {
      const email = (b.email || '').trim()
      if (b.settings.mail_to_broker && email) return { direct: true, to: email, label: `direct → ${email}` }
      return { direct: false, to: this.mailContact, label: `validation → ${this.mailContact}` }
    },
    confirmMail (b, s) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: s.mailed_at ? 'Renvoyer le relevé' : 'Envoyer le relevé',
        text: `Envoyer ${s.statement_no} (PDF en pièce jointe) à ${this.mailTarget(b).to} ?`,
        acceptText: 'Envoyer',
        cancelText: 'Annuler',
        accept: () => this.mailStatement(s)
      })
    },
    async mailStatement (s) {
      this.mailing = s.id
      try {
        const res = await axios.post(`/brokercommission/statements/${s.id}/mail`)
        this.notify('success', 'Relevé envoyé', `${s.statement_no} → ${res.data.mail.to}`)
      } catch (err) {
        this.notify('danger', 'Mail non parti', await errorMessage(err, 'L\'envoi a échoué.'))
      } finally {
        this.mailing = null
        await this.load()
      }
    },
    // Releve PDF : annulable tant qu'il n'est pas regle. Reglement : seulement le dernier.
    canVoid (b, s) {
      if (s.voided_at) return false
      if (s.kind === 'statement') return !s.settled_by_no
      const settlements = b.history.filter((h) => !h.voided_at && h.kind !== 'statement')
      return settlements.length > 0 && settlements[0].id === s.id
    },
    confirmVoid (s) {
      const isStatement = s.kind === 'statement'
      const statementText = `Annuler le relevé ${s.statement_no} ? Le document est annulé (le PDF reste consultable) ; rien ne change côté paiement.`
      const settlementText = `Annuler le règlement ${s.statement_no} (${this.money(s.total_commission_eur)} €) ? Ses ${s.nb_lines} facture(s) redeviennent « non payées ».`
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: isStatement ? 'Annuler le relevé' : 'Annuler le règlement',
        text: isStatement ? statementText : settlementText,
        acceptText: 'Annuler',
        cancelText: 'Garder',
        accept: () => this.voidStatement(s)
      })
    },
    async voidStatement (s) {
      try {
        await axios.delete(`/brokercommission/statements/${s.id}`)
        this.notify('success', 'Relevé annulé', s.statement_no)
        await this.load()
      } catch (err) {
        this.notify('danger', 'Annulation refusée', await errorMessage(err, 'L\'annulation a échoué.'))
      }
    },

    // ---- Parametres ----
    openSettings (b) {
      this.settingsForm = Object.assign({ idbroker: b.idbroker, name: b.name, email: b.email, idlanguage: b.idlanguage || null }, b.settings)
      this.settingsOpen = true
    },
    async saveSettings () {
      const f = this.settingsForm
      this.savingSettings = true
      try {
        await axios.put(`/brokercommission/settings/${f.idbroker}`, {
          enabled: !!f.enabled,
          trigger_mode: f.trigger_mode,
          transport_eur_per_m3: Number(f.transport_eur_per_m3),
          pct_default: Number(f.pct_default),
          pct_aboute: Number(f.pct_aboute),
          start_date: f.start_date,
          mail_to_broker: !!f.mail_to_broker,
          idlanguage: f.idlanguage || null
        })
        this.settingsOpen = false
        this.notify('success', 'Règles enregistrées', f.name)
        await this.load()
      } catch (err) {
        this.notify('danger', 'Règles non enregistrées', await errorMessage(err, 'L\'enregistrement a échoué.'))
      } finally {
        this.savingSettings = false
      }
    },

    // ---- Affichage ----
    // Lien depuis la synthese : deplie la section impayes du broker.
    openUnpaid (b) {
      this.$set(this.open, `u${  b.idbroker}`, true)
    },
    toggleOpen (kind, id) {
      this.$set(this.open, kind + id, !this.open[kind + id])
    },
    isOpen (kind, id) {
      return !!this.open[kind + id]
    },
    sum (rows, field) {
      return sumCents(rows, field)
    },
    money (value, decimals) {
      const n = Number(value) || 0
      return decimals === 0 ? MONEY0.format(n) : MONEY.format(n)
    },
    pct (value) {
      if (value === null || value === undefined) return '-'
      return `${String(Math.round(Number(value) * 10) / 10).replace('.', ',')  } %`
    },
    formatDate (value) {
      if (!value) return '-'
      const [y, m, d] = String(value).slice(0, 10).split('-')
      return `${d}/${m}/${y}`
    },
    shortDate (value) {
      if (!value) return '-'
      const [y, m, d] = String(value).slice(0, 10).split('-')
      return `${d}/${m}/${y.slice(2)}`
    },
    hasRon (b) {
      return b.eligible.some((r) => r.currency === 'RON')
    },
    formatDateTime (value) {
      if (!value) return '-'
      const d = new Date(value)
      return isNaN(d.getTime()) ? '-' : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    },
    initials (name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      return ((parts[0] || '?')[0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
    },
    triggerLabel (mode) {
      return mode === 'payment' ? 'Au paiement' : 'À la facturation'
    },
    typeLabel (type) {
      return TYPE_LABELS[type] || type
    },
    explain (r) {
      const parts = []
      if (r.method === 'mirror' && r.doc_type === 'creditnote') {
        parts.push(`Miroir de la facture d'origine #${r.idsource} (ratio ${r.ratio})`)
      } else if (r.method === 'mirror' && r.doc_type === 'advance') {
        parts.push(`Part du devis #${r.idsource} au prorata de l'acompte (ratio ${r.ratio})`)
      }
      if ((r.detail || []).some((d) => d.kind === 'advance_deduction')) parts.push('Déduction d\'acompte = commission de l\'acompte')
      if ((r.detail || []).some((d) => d.aboute)) parts.push('Contient des produits aboutés')
      const w = r.warnings || []
      if (w.includes('goods_without_volume')) parts.push('⚠ Produit sans volume : transport non déduit sur cette ligne')
      if (w.includes('base_negative')) parts.push('⚠ CA hors transport négatif')
      if (w.includes('creditnote_source_missing')) parts.push('⚠ Facture d\'origine introuvable : taux normal sans transport')
      if (w.includes('quotation_missing')) parts.push('⚠ Devis introuvable : taux normal sans transport')
      if (w.includes('advance_not_found')) parts.push('⚠ Acompte introuvable : déduction au taux normal')
      if (r.requires) parts.push('Avoir lié à une facture de cette liste : elles partent ensemble')
      return parts.join(' · ')
    },
    notify (color, title, text) {
      this.$vs.notify({ title, text, color, iconPack: 'feather', icon: color === 'success' ? 'icon-check' : 'icon-alert-circle', position: 'bottom-right', time: 8000 })
    }
  },
  created () {
    if (!(localStorage.getItem('login-roles') || '').toLowerCase().includes('admin')) {
      this.notify('danger', 'Denied', 'Redirection')
      this.$router.push({ path: '/' })
      return
    }
    this.load()
  }
}
</script>

<style scoped>
/* ---- Barre d'outils ---- */
.bcp-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.06);
}
.bcp-toolbar-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}
.bcp-period {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  color: #475569;
  font-size: 0.875rem;
}
.bcp-period-icon {
  color: #94a3b8;
}
.bcp-date {
  height: 32px;
  padding: 0 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  color: #1e293b;
  font-family: inherit;
  font-size: 0.875rem;
}
.bcp-date:focus {
  outline: none;
  border-color: rgba(var(--vs-primary), 1);
  box-shadow: 0 0 0 2px rgba(var(--vs-primary), 0.15);
}
.bcp-filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}
.bcp-segmented {
  display: inline-flex;
  padding: 3px;
  border-radius: 8px;
  background: #f1f5f9;
}
.bcp-segmented button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #475569;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.bcp-segmented button:hover {
  color: #1e293b;
}
.bcp-segmented button.active {
  background: #fff;
  color: rgba(var(--vs-primary), 1);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}
.bcp-count {
  display: inline-block;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #e2e8f0;
  color: #475569;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
.bcp-segmented button.active .bcp-count {
  background: rgba(var(--vs-primary), 0.15);
  color: rgba(var(--vs-primary), 1);
}
.bcp-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 4px 18px;
  color: #64748b;
  font-size: 0.8125rem;
}

/* ---- Etats vides / chargement ---- */
.bcp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 3rem 1rem;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  text-align: center;
}
.bcp-empty-inline {
  padding: 16px;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
}
.bcp-spin {
  animation: bcp-spin 1s linear infinite;
}
@keyframes bcp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ---- Chiffres cles ---- */
.bcp-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 2.2rem;
}
.bcp-kpi {
  padding: 14px 18px;
  border-left: 4px solid #94a3b8;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.06);
}
.bcp-kpi-label {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.bcp-kpi-value {
  margin: 4px 0 2px;
  color: #1e293b;
  font-size: 1.5rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.bcp-kpi-sub {
  color: #64748b;
  font-size: 0.75rem;
}
.kpi-topay {
  border-left-color: #ef6c00;
}
.kpi-future {
  border-left-color: #f0b429;
}
.kpi-paid {
  border-left-color: #2e7d32;
}
.kpi-unpaid {
  border-left-color: #c62828;
}

/* ---- Cartes ---- */
.bcp-note {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.8125rem;
  line-height: 1.45;
}
.bcp-note .feather-icon {
  flex: none;
  margin-top: 1px;
}
.bcp-muted {
  color: #94a3b8;
}
.bcp-sub {
  color: #94a3b8;
  font-weight: 400;
}
.bcp-broker {
  scroll-margin-top: 110px;
}
.bcp-broker-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 24px;
  margin-bottom: 16px;
}
.bcp-broker-id {
  display: flex;
  align-items: center;
  gap: 14px;
}
.bcp-avatar {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(var(--vs-primary), 0.12);
  color: rgba(var(--vs-primary), 1);
  font-weight: 700;
}
.bcp-broker-name {
  margin-right: 10px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}
.bcp-rules {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 4px;
  color: #64748b;
  font-size: 0.8125rem;
}
.bcp-rules > span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.bcp-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
}
.bcp-broker >>> .con-vs-alert .vs-alert {
  font-size: 0.875rem;
}

/* ---- Pastilles ---- */
.bcp-pill {
  display: inline-block;
  padding: 2px 8px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
.pill-primary {
  background: rgba(var(--vs-primary), 0.12);
  color: rgba(var(--vs-primary), 1);
}
.pill-warning {
  background: #fff3e0;
  color: #e65100;
}
.pill-warning-outline {
  border-color: #ef6c00;
  background: #fff;
  color: #e65100;
}
.pill-amber {
  background: #fff8e1;
  color: #8d6e63;
}
.pill-success {
  background: #e8f5e9;
  color: #2e7d32;
}
.pill-danger {
  background: #ffebee;
  color: #c62828;
}
.pill-info {
  background: #e3f2fd;
  color: #1565c0;
}
.pill-muted {
  background: #f1f5f9;
  color: #64748b;
}

/* ---- Tableaux ---- */
.bcp-scroll {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.bcp-card .overflow-x-auto {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.bcp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.bcp-table th {
  padding: 8px 8px;
  background: #455a64;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: left;
  vertical-align: bottom;
  white-space: normal;
}
.bcp-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}
.bcp-table tbody tr:nth-child(even) td {
  background: #f8fafc;
}
.bcp-table tbody tr:hover td {
  background: #f1f5f9;
}
.bcp-table .num {
  text-align: right;
}
.bcp-table .strong {
  font-weight: 700;
}
.bcp-table .chk {
  width: 32px;
  text-align: center;
}
.bcp-table input[type='checkbox'] {
  width: 15px;
  height: 15px;
  vertical-align: middle;
  cursor: pointer;
  accent-color: rgba(var(--vs-primary), 1);
}
.bcp-table td.client {
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Colonne COMMISSION toujours visible, meme quand le tableau defile. */
.bcp-table .sticky-end {
  position: sticky;
  right: 0;
  z-index: 1;
  box-shadow: -1px 0 0 #e2e8f0;
}
.bcp-table th.sticky-end {
  z-index: 2;
}
.bcp-comm {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.bcp-table tbody tr.bcp-total td {
  border-top: 2px solid #94a3b8;
  background: #e8f5e9;
  font-weight: 700;
}
.bcp-table tbody tr.bcp-total-waiting td {
  background: #fff8e1;
}
.bcp-table tbody tr.bcp-total-paid td {
  background: #e3f2fd;
}
.bcp-table tr.bcp-off td {
  opacity: 0.5;
}
.bcp-table tr.bcp-dim td {
  color: #64748b;
}
.bcp-table tr.bcp-voided td {
  color: #94a3b8;
  text-decoration: line-through;
}
.bcp-table td.actions {
  text-decoration: none;
}
.bcp-summary td {
  padding: 8px 10px;
  font-size: 0.8125rem;
}
.bcp-summary th {
  padding: 8px 10px;
}

/* ---- Sections repliables ---- */
.bcp-section {
  margin-top: 16px;
}
.bcp-section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  color: #1e293b;
  font-family: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}
.bcp-section-head:hover {
  background: #f1f5f9;
}
.bcp-section-title {
  font-weight: 700;
}
.bcp-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: auto;
}
.bcp-stat {
  padding: 2px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #475569;
  font-size: 0.75rem;
  white-space: nowrap;
}
.bcp-stat.stat-danger {
  border-color: #ffcdd2;
  background: #ffebee;
  color: #c62828;
}
.bcp-stat.stat-warning {
  border-color: #ffe0b2;
  background: #fff3e0;
  color: #e65100;
}

/* ---- Couleurs de texte ---- */
.pay-paid {
  color: #2e7d32;
}
.pay-partial {
  color: #ef6c00;
}
.pay-pending {
  color: #c62828;
}
/* Meme badge que l'ecran Overdue (DashboardFinance.vue), sans l'animation. */
.dispute-badge {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #e53935;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

/* ---- Parametres ---- */
.bcp-select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  background: #fff;
}
.bcp-form {
  padding: 0.5rem;
}
.bcp-help {
  color: #64748b;
  font-size: 0.8125rem;
}
</style>
